import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Injectable,
  Input,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CalendarDateFormatter,
  CalendarEventTitleFormatter,
  CalendarView,
  DateFormatterParams,
  CalendarEvent as NgCalendarEvent
} from 'angular-calendar';
import { DAYS_OF_WEEK, WeekViewHourSegment } from 'calendar-utils';
import { I18nService } from 'i18n';
import moment from 'moment';
import { map, Observable, of, Subject, Subscription } from 'rxjs';
import { darkenColor, Logger } from 'shared';
import { CalendarService } from "../../calendar.service";
import { CalendarEvent } from '../../models/event';

// weekStartsOn option is ignored when using moment, 
// as it needs to be configured globally for the moment locale
moment.updateLocale('en', {
  week: {
    dow: DAYS_OF_WEEK.MONDAY,
    doy: 0
  },
});

@Injectable()
export class CustomDateFormatter extends CalendarDateFormatter {
  public override dayViewHour({ date, locale }: DateFormatterParams): string {
    return formatDate(date, 'HH:mm', locale ?? 'en-US');
  }

  public override weekViewHour({ date, locale }: DateFormatterParams): string {
    return this.dayViewHour({ date, locale });
  }
}

// https://mattlewis92.github.io/angular-calendar/#/disable-tooltips
@Injectable()
export class CustomEventTitleFormatter extends CalendarEventTitleFormatter {
  public override monthTooltip(event: NgCalendarEvent, title: string): string {
    return '';
  }

  public override weekTooltip(event: NgCalendarEvent, title: string): string {
    return '';
  }

  public override dayTooltip(event: NgCalendarEvent<any>, title: string): string {
    return '';
  }
}

@Component({
  selector: 'calendar',
  styleUrls: ['calendar.component.scss'],
  templateUrl: 'calendar.component.html',
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter
    },
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter,
    }
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent {
  
  protected static CALENDAR_RESPONSIVE_BREAKPOINTS = {
    small: {
      breakpoint: '(max-width: 576px)',
      daysInWeek: 2,
      monthViewFontSize: '0.7em'
    },
    medium: {
      breakpoint: '(max-width: 768px)',
      daysInWeek: 3,
      monthViewFontSize: '0.8em'
    },
    large: {
      breakpoint: '(max-width: 960px)',
      daysInWeek: 5,
      monthViewFontSize: '0.9em'
    },
  };

  @Input() entities$: Observable<CalendarEvent[]> = of([]);
  @Input() date = new Date();
  @Input() locale = this.i18nService.getLanguage();

  @Output() dayEventsSelect = new EventEmitter<{day: Date, events: CalendarEvent[]}>();
  @Output() daySelect = new EventEmitter<Date>();
  @Output() eventSelectionChanged = new EventEmitter<{ index: number; event: CalendarEvent; }>();

  CalendarView = CalendarView;
  view: CalendarView = this.calendarViewService.view;

  daysInWeek = 7;
  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;

  monthViewFontSize = signal<string>('1em');
  monthViewStartDate = signal<Date>(new Date());
  monthViewEndDate = signal<Date>(new Date());
  monthViewDays = signal<number>(0);
  monthViewRows = signal<number>(0);

  events = signal<NgCalendarEvent[]>([]);
  refresh = new Subject<void>();

  darkenColor = darkenColor;

  protected eventsById: { [id: string]: { index: number; event: CalendarEvent; }} = {};
  protected subscription = new Subscription();

  protected logger = new Logger('CalendarComponent');

  constructor(
    protected breakpointObserver: BreakpointObserver, 
    protected destroyRef: DestroyRef,
    protected i18nService: I18nService,
    protected calendarViewService: CalendarService
  ) {
    const breakpoints = Object.values(CalendarComponent.CALENDAR_RESPONSIVE_BREAKPOINTS);
    this.breakpointObserver
      .observe(breakpoints.map(({ breakpoint }) => breakpoint))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: BreakpointState) => {
        const foundBreakpoint = breakpoints.find(({ breakpoint }) => !!state.breakpoints[breakpoint]);
        if (foundBreakpoint) {
          this.daysInWeek = foundBreakpoint.daysInWeek;
          this.monthViewFontSize.set(foundBreakpoint.monthViewFontSize);
        } else {
          this.daysInWeek = 7;
          this.monthViewFontSize.set('1em');
        }
      });

    // Subscribe to currentLanguage$ to update locale and moment locale
    this.i18nService.currentLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lang => {
        this.locale = lang;
        // Update moment locale for week start configuration
        moment.updateLocale(lang, {
          week: {
            dow: DAYS_OF_WEEK.MONDAY,
            doy: 0
          }
        });
        // Refresh calendar to reflect locale changes
        this.refresh.next();
      });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.entities$?.pipe(
        map(events => {
          this.eventsById = {};
          this.logger.debug('EVENTS > RECEIVED', events);
          return events?.reduceRight((result, event, index) => {
            if (event) {
              const ngEvent = this.transformCalendarEvent(event);
              this.eventsById[this.uniqueEventId(event)] = { index, event };
              result.unshift(ngEvent);
            }
            return result;
          }, [] as NgCalendarEvent[])
        })
      )
      .subscribe((events: NgCalendarEvent[]) => {
        this.logger.debug('EVENTS > TRANSFORMED', events);
        this.events.set(events);
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date'] && changes['date']?.currentValue) {
      this.updateCalendarProperties();
    }
  }

  private updateCalendarProperties(): void {
    this.logger.debug('updateCalendarProperties', this.date);
    const inputMoment = moment(this.date);

    if (!inputMoment.isValid()) {
      this.logger.error('Invalid date provided');
      return;
    }

    const firstOfMonth = inputMoment.clone().startOf('month');
    const calendarStart = firstOfMonth.clone().startOf('isoWeek');
    const endOfMonth = inputMoment.clone().endOf('month');
    const calendarEnd = endOfMonth.clone().endOf('isoWeek');
    const days = calendarEnd.diff(calendarStart, 'days') + 1;
    const rows = Math.ceil(days / this.daysInWeek);

    this.monthViewStartDate.set(calendarStart.toDate());
    this.monthViewEndDate.set(calendarEnd.toDate());
    this.monthViewDays.set(days);
    this.monthViewRows.set(rows);
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  onDayClicked(date: Date) {
    // this.daySelect.emit(date);
    const startTime = date.getTime();
    const endTime   = moment(startTime).add(24, "hours").toDate().getTime();
    const events    = Object.values(this.eventsById).filter((item)=>{
      return item.event.timeTo>startTime && item.event.timeFrom<endTime;
    }).map(item => item.event);
    this.dayEventsSelect.emit({ day: date, events });
  }

  onCalendarEventSelected(event: {event: NgCalendarEvent, sourceEvent: MouseEvent | KeyboardEvent}) {
    this.logger.debug('onCalendarEventSelected', event);
    const pick = ({ event }: {event: NgCalendarEvent, sourceEvent: MouseEvent | KeyboardEvent}) => ({id: event.id});
    const {id} = pick(event);
    if (id) {
      const entry = this.eventsById[id];
      if (entry?.event) {
        this.eventSelectionChanged.emit(entry);
      }
    }
  }

  onSegmentMouseDown(segment: WeekViewHourSegment, segmentElement: HTMLElement) {
    this.logger.debug('onSegmentMouseDown', {segment, segmentElement});
  }

  onSegmentTouchStart(segment: WeekViewHourSegment, segmentElement: HTMLElement, event: TouchEvent) {
    this.logger.debug('onSegmentTouchStart', {segment, segmentElement, event});
  }

  protected transformCalendarEvent(calendarEvent: CalendarEvent): NgCalendarEvent {
    const event: NgCalendarEvent = calendarEvent ? {
      id: this.uniqueEventId(calendarEvent),
      start: new Date(calendarEvent.timeFrom),
      end: new Date(calendarEvent.timeTo),
      title: calendarEvent.name,
      color: {
        primary: calendarEvent.color ?? '#1e90ff',
        secondary: '#D1E8FF',
        secondaryText: 'white'
      },
      // actions?: EventAction[];
      allDay: false,
      cssClass: 'event',
      resizable: {
        beforeStart: false,
        afterEnd: false
      },
      draggable: false,
      meta: { id: calendarEvent.id, media: calendarEvent.media }
    } : calendarEvent as unknown as NgCalendarEvent;
    return event;
  }

  uniqueEventId(event: CalendarEvent) {
    // recurring events have the same id, 
    // create unique keys (e.g. for eventsById map) by appending the start and end time
    return `${event.id}_${event.timeFrom}_${event.timeTo}`;
  }
}